import { FC, useMemo } from 'react'
import { CellProps } from 'react-table'
import {
  CustomTable,
  PlainHeader,
  RowActions,
  StackedCell,
  StatusBadge,
  TableColumn,
  TableEmptyState,
} from 'app/components'
import { IconPaths } from '_metronic/helpers'
import { LotacaoView } from '../../core/_lotacaoModels'

type Handlers = {
  onEdit: (lotacao: LotacaoView) => void
  onDelete: (lotacao: LotacaoView) => void
  onReactivate: (lotacao: LotacaoView) => void
}

type Props = Handlers & {
  lotacoes: LotacaoView[]
  /** Sem `Lotacao:create`/`update`/`delete` a coluna de ações não é renderizada. */
  podeEditar: boolean
}

const LotacaoActionsCell: FC<Handlers & { lotacao: LotacaoView }> = ({
  lotacao,
  onEdit,
  onDelete,
  onReactivate,
}) => {
  const itemName = lotacao.unidade?.nome || 'lotação'

  return lotacao.ativo ? (
    <RowActions
      itemName={itemName}
      primaryAction={{ label: 'Editar', onClick: () => onEdit(lotacao), icon: IconPaths.Pen }}
      items={[{ label: 'Inativar', onClick: () => onDelete(lotacao), danger: true }]}
    />
  ) : (
    <RowActions
      itemName={itemName}
      primaryAction={{ label: 'Reativar', onClick: () => onReactivate(lotacao) }}
    />
  )
}

const lotacoesColumns = (
  podeEditar: boolean,
  handlers: Handlers
): ReadonlyArray<TableColumn<LotacaoView>> => [
  {
    Header: () => <PlainHeader>Unidade</PlainHeader>,
    id: 'unidade',
    Cell: ({ row }: CellProps<LotacaoView>) => (
      <StackedCell
        primary={row.original.unidade?.nome}
        secondary={row.original.unidade?.cnes ? `CNES ${row.original.unidade.cnes}` : undefined}
      />
    ),
  },
  {
    Header: () => <PlainHeader>CBO</PlainHeader>,
    id: 'cbo',
    Cell: ({ row }: CellProps<LotacaoView>) => {
      const { cbo } = row.original
      return cbo ? (
        <StackedCell primary={cbo.descricao || cbo.nome} secondary={cbo.codigo} />
      ) : (
        <StackedCell />
      )
    },
  },
  {
    Header: () => <PlainHeader>INE</PlainHeader>,
    id: 'ine',
    cellWidth: '120px',
    hideOnMobile: true,
    Cell: ({ row }: CellProps<LotacaoView>) => <StackedCell primary={row.original.ine} tabular />,
  },
  {
    Header: () => <PlainHeader>Status</PlainHeader>,
    id: 'ativo',
    cellWidth: '112px',
    Cell: ({ row }: CellProps<LotacaoView>) => (
      <StatusBadge active={row.original.ativo} labels={['Ativa', 'Inativa']} />
    ),
  },
  ...(podeEditar
    ? [
        {
          Header: () => <PlainHeader>Ações</PlainHeader>,
          id: 'actions',
          cellWidth: '112px',
          align: 'end' as const,
          Cell: ({ row }: CellProps<LotacaoView>) => (
            <LotacaoActionsCell lotacao={row.original} {...handlers} />
          ),
        },
      ]
    : []),
]

export const LotacoesTable: FC<Props> = ({
  lotacoes,
  podeEditar,
  onEdit,
  onDelete,
  onReactivate,
}) => {
  const columns = useMemo(
    () => lotacoesColumns(podeEditar, { onEdit, onDelete, onReactivate }),
    [podeEditar, onEdit, onDelete, onReactivate]
  )

  return (
    <CustomTable
      id='table_lotacoes'
      ariaLabel='Lotações do profissional'
      data={lotacoes}
      columns={columns}
      emptyState={
        <TableEmptyState
          title='Nenhuma lotação cadastrada'
          description={
            podeEditar
              ? 'Use "Nova lotação" para vincular o profissional a uma unidade.'
              : 'Este profissional ainda não tem lotações.'
          }
        />
      }
    />
  )
}
